/* =============================================================================
 *  useEmailBodies — lazily fetch full email bodies (plain text) for the big Wall
 *  tiles, so the email preview fills with the REAL body instead of the short
 *  list snapshot. Reads email_bodies/{id} first (one fast Firestore read), with a
 *  Graph cloud-function fallback; results are LRU-cached + deduped by the shared
 *  graphEmailBodyCache, and degrade to empty (→ caller keeps the snippet) when a
 *  body can't be resolved. Only call with the handful of ids that show a preview.
 *
 *  Hygiene: the returned map is pruned to the current id set (no unbounded growth
 *  over a long session), and ids whose fetch hit a real backend error are not
 *  re-hit on every wall reorder.
 * ============================================================================= */

import { useEffect, useRef, useState } from 'react';
import { getCachedGraphEmailBody, getOrFetchGraphEmailBody } from '../../pages/mail/utils/graphEmailBodyCache';
import { cleanEmailText } from './feedModel';

const CAP = 1500; // plenty for a faded big tile; keeps state light

export function useEmailBodies(ids: string[]): Record<string, string> {
  const [bodies, setBodies] = useState<Record<string, string>>({});
  // Ids whose fetch hit a real (non-404) backend error — don't re-hit a failing
  // endpoint on every reorder. (The cache's circuit breaker only guards the mail
  // feed's prefetch path, not this getOrFetch path.) 404s are already suppressed
  // inside the cache, and resolve to empty content here.
  const failed = useRef<Set<string>>(new Set());
  const key = ids.join(',');

  useEffect(() => {
    let cancelled = false;
    const idSet = new Set(ids.filter(Boolean));

    // Prune bodies that scrolled off, so the map stays proportional to the screen.
    setBodies((prev) => {
      let changed = false;
      const next: Record<string, string> = {};
      for (const id of Object.keys(prev)) {
        if (idSet.has(id)) next[id] = prev[id]; else changed = true;
      }
      return changed ? next : prev;
    });

    const apply = (id: string, body: { content?: string } | null) => {
      if (cancelled || !body) return;
      const txt = cleanEmailText(body.content).slice(0, CAP);
      if (txt) setBodies((prev) => (prev[id] === txt ? prev : { ...prev, [id]: txt }));
    };
    for (const id of Array.from(idSet)) {
      const cached = getCachedGraphEmailBody(id, 'text');
      if (cached) { apply(id, cached); continue; }
      if (failed.current.has(id)) continue; // already failed this session — skip
      // getOrFetch dedupes inflight requests, so calling it across reorders is safe.
      getOrFetchGraphEmailBody(id, 'text')
        .then((b) => apply(id, b))
        .catch(() => { failed.current.add(id); /* keep snippet */ });
    }
    return () => { cancelled = true; };
    // key is the stable signature of `ids`; reconcile/fetch only when it changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return bodies;
}
